/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaCar, FaSearch, FaCalendarCheck, FaCheckCircle, FaTimesCircle, FaClock, FaTrash } from 'react-icons/fa';
import '../../pages_premium.css';
import '../PropertyRequests.css';
import './CarsList.css';
import { getFullImageUrl } from '../../utils/url';
import AdminPagination from '../../components/common/AdminPagination';

export default function CarBookingsList() {
    const [bookings, setBookings] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/admin/car-bookings');
            setBookings(res.data || []);
        } catch (error) {
            console.error('Error fetching car bookings:', error);
            toast.error('Failed to load car bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await axiosInstance.put(`/admin/car-bookings/${id}/status`, { status: newStatus });
            toast.success(`Booking status updated to ${newStatus}`);
            fetchBookings();
        } catch (error) {
            console.error('Error updating booking status:', error);
            toast.error(error.response?.data?.message || 'Failed to update booking status');
        }
    };

    const handleDeleteBooking = async (id) => {
        if (!window.confirm('Are you sure you want to delete this car booking / inspection entry?')) {
            return;
        }

        try {
            await axiosInstance.delete(`/admin/car-bookings/${id}`);
            toast.success('Car booking deleted successfully');
            fetchBookings();
        } catch (error) {
            console.error('Error deleting car booking:', error);
            toast.error(error.response?.data?.message || 'Failed to delete car booking');
        }
    };

    const formatDate = (val) => {
        if (!val) return '-';
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return String(val);
    };

    const getRoleBadgeClass = (roleStr) => {
        if (!roleStr) return 'role-badge role-badge-lister';
        const r = roleStr.toLowerCase();
        if (r === 'business owner' || r === 'owner') return 'role-badge role-badge-owner';
        if (r === 'student') return 'role-badge role-badge-student';
        return 'role-badge role-badge-lister';
    };

    const getStatusBadge = (status, listingType) => {
        const s = String(status || '').toLowerCase();
        if (s === 'completed') {
            return (
                <span className="status-badge status-badge-accepted">
                    <FaCheckCircle size={11} /> Completed
                </span>
            );
        }
        if (s === 'cancelled') {
            return (
                <span className="status-badge status-badge-rejected">
                    <FaTimesCircle size={11} /> Cancelled
                </span>
            );
        }
        const isSelling = listingType === 'selling' || listingType === 'sale';
        return (
            <span className="status-badge status-badge-pending">
                <FaClock size={11} /> {isSelling ? 'Inspection Booked' : 'Rental Booked'}
            </span>
        );
    };

    const filtered = bookings.filter(b =>
        b.car_title?.toLowerCase().includes(search.toLowerCase()) ||
        b.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        b.listed_by?.toLowerCase().includes(search.toLowerCase()) ||
        b.user_email?.toLowerCase().includes(search.toLowerCase()) ||
        b.status?.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const paginatedBookings = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper cars-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                            <FaCalendarCheck size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Car Bookings & Inspections</h1>
                            <p className="ph-subtitle">Track and manage all scheduled vehicle rentals and sales inspections</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{bookings.length}</span>
                            <span className="count-lbl">Bookings & Inspections</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Car Bookings / Inspections Schedule
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by car, user, lister, status..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>#</th>
                                    <th style={{ minWidth: '220px' }}>Car Details</th>
                                    <th style={{ minWidth: '180px' }}>Interested User</th>
                                    <th style={{ minWidth: '160px' }}>Listed By</th>
                                    <th style={{ minWidth: '130px' }}>Listing Type</th>
                                    <th style={{ minWidth: '220px', whiteSpace: 'nowrap' }}>Scheduled Date & Time</th>
                                    <th style={{ minWidth: '180px', whiteSpace: 'nowrap' }}>Status</th>
                                    <th className="text-center" style={{ minWidth: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>Loading bookings...</p>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="8">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">📅</div>
                                                <p>No car bookings or inspections found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedBookings.map((b, idx) => {
                                        const isSelling = b.car_listing_type === 'selling' || b.car_listing_type === 'sale';
                                        const defaultStatus = isSelling ? 'Inspection Booked' : 'Rental Booked';

                                        return (
                                            <tr key={b.id}>
                                                <td style={{ color: '#9ba6c0', fontWeight: 600 }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2.5">
                                                        {b.car_thumbnail ? (
                                                            <img
                                                                src={getFullImageUrl(b.car_thumbnail)}
                                                                alt="Car"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                            />
                                                        ) : (
                                                            <div className="d-flex align-items-center justify-content-center bg-light rounded text-muted" style={{ width: '48px', height: '48px' }}>
                                                                <FaCar size={20} />
                                                            </div>
                                                        )}
                                                        <div className="d-flex flex-column">
                                                            <Link to={`/cars/${b.car_id}`} style={{ fontWeight: 700, color: '#0f172a', textDecoration: 'none' }}>
                                                                {b.car_title}
                                                            </Link>
                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                                ID: #{b.car_id}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-0.5">
                                                        <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{b.user_name}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>✉️ {b.user_email}</span>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>📞 {b.user_phone}</span>
                                                        <span className={getRoleBadgeClass(b.user_role)} style={{ width: 'fit-content', fontSize: '11px', padding: '2px 8px', marginTop: '2px' }}>
                                                            {b.user_role}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-0.5">
                                                        <strong style={{ color: '#334155', fontSize: '0.88rem' }}>{b.listed_by}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>✉️ {b.owner_email}</span>
                                                        <span className={getRoleBadgeClass(b.listed_type)} style={{ width: 'fit-content', fontSize: '11px', padding: '2px 8px', marginTop: '2px' }}>
                                                            {b.listed_type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <span className={`badge ${isSelling ? 'badge-type-selling' : 'badge-type-rental'} px-2.5 py-1`} style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                        {isSelling ? '🏷️ Selling Inspection' : '🚗 Rental Booking'}
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <div className="d-flex align-items-center gap-1.5" style={{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>
                                                        <strong style={{ color: '#0f172a' }}>📅 {formatDate(b.booking_date)}</strong>
                                                        <span style={{ color: '#64748b' }}>• ⏰ {b.booking_time}</span>
                                                    </div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {getStatusBadge(b.status, b.car_listing_type)}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center align-items-center gap-1.5">
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: '130px', fontSize: '12px', borderRadius: '6px', borderColor: '#cbd5e1' }}
                                                            value={b.status || defaultStatus}
                                                            onChange={(e) => handleUpdateStatus(b.id, e.target.value)}
                                                        >
                                                            <option value={defaultStatus}>{defaultStatus}</option>
                                                            <option value="completed">Completed</option>
                                                            <option value="cancelled">Cancelled</option>
                                                        </select>
                                                        <button
                                                            type="button"
                                                            className="action-btn action-btn-delete"
                                                            onClick={() => handleDeleteBooking(b.id)}
                                                            title="Delete Booking"
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="car bookings"
                    />
                </div>

            </div>
        </div>
    );
}
